var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from "react";
import cn from "classnames";
import { AppBarNav } from "@react-md/app-bar";
import { useIcon } from "@react-md/icon";
import { DEFAULT_SHEET_TIMEOUT } from "@react-md/sheet";
import { useCSSTransition, } from "@react-md/transition";
import { DEFFAULT_LAYOUT_NAV_TOGGLE_CLASSNAMES } from "./constants";
import { useLayoutConfig } from "./LayoutProvider";
import { isTemporaryLayout, isToggleableLayout } from "./utils";
/**
 * This is the default implementation for the navigation toggle button within
 * the `Layout` component and should normally be the first child for the
 * `LayoutAppBar`. In addition, this component will automatically update itself
 * to provide an accessible `aria-label` for screen readers and change to a
 * toggle button for toggleable layouts.
 */
export var LayoutNavToggle = forwardRef(function LayoutNavToggle(_a, nodeRef) {
    var propAriaLabel = _a["aria-label"], propAriaPressed = _a["aria-pressed"], propChildren = _a.children, className = _a.className, _b = _a.buttonType, buttonType = _b === void 0 ? "icon" : _b, onClick = _a.onClick, propOffset = _a.offset, rendered = _a.rendered, propTabIndex = _a.tabIndex, _c = _a.timeout, timeout = _c === void 0 ? DEFAULT_SHEET_TIMEOUT : _c, _d = _a.classNames, classNames = _d === void 0 ? DEFFAULT_LAYOUT_NAV_TOGGLE_CLASSNAMES : _d, props = __rest(_a, ["aria-label", "aria-pressed", "children", "className", "buttonType", "onClick", "offset", "rendered", "tabIndex", "timeout", "classNames"]);
    var icon = useIcon("menu");
    var _e = useLayoutConfig(), baseId = _e.baseId, layout = _e.layout, showNav = _e.showNav, hideNav = _e.hideNav, visible = _e.visible;
    var isToggleable = isToggleableLayout(layout);
    var isTemporary = isTemporaryLayout(layout);
    var isRendered = rendered;
    if (typeof isRendered === "undefined") {
        isRendered = isTemporary || isToggleable;
    }
    var offset = propOffset;
    if (typeof offset === "undefined") {
        offset = isToggleable && visible;
    }
    var elementProps = useCSSTransition({
        nodeRef: nodeRef,
        transitionIn: offset,
        temporary: false,
        timeout: timeout,
        classNames: classNames,
        className: cn("rmd-layout-nav-toggle", className),
    }).elementProps;
    if (!isRendered) {
        return null;
    }
    var children = propChildren;
    if (buttonType === "icon" && typeof children === "undefined") {
        children = icon;
    }
    var tabIndex = propTabIndex;
    if (typeof tabIndex === "undefined" && visible && isToggleable) {
        // set to -1 so it isn't tab focusable but is still programmatically
        // focusable for temporary navigation drawers to re-focus once closed
        tabIndex = -1;
    }
    var ariaPressed = propAriaPressed;
    if (typeof ariaPressed === "undefined" && isToggleable) {
        ariaPressed = visible;
    }
    var ariaLabel = propAriaLabel;
    if (typeof ariaLabel === "undefined" &&
        typeof props["aria-labelledby"] === "undefined") {
        ariaLabel = isToggleable ? "Navigation" : "Show Navigation";
    }
    return (_jsx(AppBarNav, __assign({ id: "".concat(baseId, "-nav-toggle") }, props, elementProps, { "aria-label": ariaLabel, "aria-pressed": ariaPressed, onClick: function (event) {
            if (onClick) {
                onClick(event);
            }
            if (!visible) {
                showNav();
            }
            else {
                hideNav();
            }
        }, buttonType: buttonType, tabIndex: tabIndex }, { children: children }), void 0));
});
//# sourceMappingURL=LayoutNavToggle.js.map